/**
 * Custom Topic Dialog
 *
 * Dialog for creating or editing a custom topic. Supports a long (up to 5000
 * word) description and uploading PDF / Word / text documents whose text becomes
 * knowledge Luno can learn from.
 */

import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  useCreateCustomTopic,
  useUpdateCustomTopic,
  useUploadTopicDocument,
  type CustomTopic,
} from '@/hooks/queries/useTopicPosts';
import { toast } from 'sonner';
import { Loader2, Upload, FileText, X } from 'lucide-react';

interface CustomTopicDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  childId: string;
  topic?: CustomTopic | null;
  onSuccess?: () => void;
}

const MAX_DESCRIPTION_WORDS = 5000;
const MAX_DOC_BYTES = 10 * 1024 * 1024; // 10MB
const ACCEPTED_DOC_EXT = ['pdf', 'docx', 'txt'];

const countWords = (s: string) => (s.trim() ? s.trim().split(/\s+/).length : 0);

export function CustomTopicDialog({
  open,
  onOpenChange,
  childId,
  topic,
  onSuccess,
}: CustomTopicDialogProps) {
  const createTopic = useCreateCustomTopic();
  const updateTopic = useUpdateCustomTopic();
  const uploadDoc = useUploadTopicDocument();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isEditing = !!topic;
  const descriptionWords = countWords(description);

  // Populate form when editing
  useEffect(() => {
    if (topic) {
      setName(topic.name);
      setDescription(topic.description || '');
      setIcon(topic.icon || '');
    } else {
      resetForm();
    }
    setFiles([]);
  }, [topic, open]);

  const resetForm = () => {
    setName('');
    setDescription('');
    setIcon('');
    setFiles([]);
  };

  const handleDescriptionChange = (value: string) => {
    // Enforce a 5000-word cap: accept as-is under the cap, otherwise keep the
    // first 5000 words.
    if (countWords(value) <= MAX_DESCRIPTION_WORDS) {
      setDescription(value);
    } else {
      setDescription(value.trim().split(/\s+/).slice(0, MAX_DESCRIPTION_WORDS).join(' '));
    }
  };

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const valid: File[] = [];
    for (const file of Array.from(incoming)) {
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!ACCEPTED_DOC_EXT.includes(ext)) {
        toast.error(`${file.name}: only PDF, Word (.docx), or text (.txt) files are allowed`);
        continue;
      }
      if (file.size > MAX_DOC_BYTES) {
        toast.error(`${file.name}: file is larger than 10MB`);
        continue;
      }
      valid.push(file);
    }
    setFiles((prev) => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;

    try {
      // Create or update the topic, and capture its id so we can attach docs.
      let topicId = topic?.id;
      if (isEditing && topic) {
        await updateTopic.mutateAsync({
          childId,
          customTopicId: topic.id,
          data: {
            name: name.trim(),
            description: description.trim() || null,
            icon: icon.trim() || null,
          },
        });
        topicId = topic.id;
      } else {
        const created = await createTopic.mutateAsync({
          childId,
          data: {
            name: name.trim(),
            description: description.trim() || undefined,
            icon: icon.trim() || undefined,
          },
        });
        topicId = created.id;
      }

      // Upload any documents to the topic.
      if (files.length > 0 && topicId) {
        setUploading(true);
        for (const file of files) {
          try {
            const result = await uploadDoc.mutateAsync({ childId, customTopicId: topicId, file });
            toast.success(result.message);
            if (result.truncated) {
              toast.info(`"${file.name}" was long — only the first part was added.`);
            }
          } catch {
            toast.error(`Failed to process "${file.name}"`);
          }
        }
      }

      onSuccess?.();
      onOpenChange(false);
      resetForm();
    } catch {
      // Error handled by mutations
    } finally {
      setUploading(false);
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) resetForm();
    onOpenChange(isOpen);
  };

  const isPending = createTopic.isPending || updateTopic.isPending || uploading;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Custom Topic' : 'Create Custom Topic'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the topic and add more content for Luno to know about.'
              : 'Create a new topic to add custom content for Luno to know about.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="topic-name">Topic Name *</Label>
            <Input
              id="topic-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Dragons, Outer Space, Cooking"
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="topic-description">Description (optional)</Label>
              <span
                className={`text-xs ${
                  descriptionWords >= MAX_DESCRIPTION_WORDS ? 'text-red-500' : 'text-muted-foreground'
                }`}
              >
                {descriptionWords.toLocaleString()} / {MAX_DESCRIPTION_WORDS.toLocaleString()} words
              </span>
            </div>
            <Textarea
              id="topic-description"
              value={description}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              placeholder="Describe this topic — you can paste up to 5000 words of content for Luno to learn from."
              rows={5}
            />
          </div>

          <div className="space-y-2">
            <Label>Documents (optional)</Label>
            <p className="text-xs text-muted-foreground">
              Upload PDF, Word (.docx), or text (.txt) files — Luno will learn from their contents.
            </p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-input px-3 py-4 text-sm text-muted-foreground transition hover:bg-muted/50"
            >
              <Upload className="h-4 w-4" />
              Choose files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt"
              multiple
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />
            {files.length > 0 && (
              <ul className="space-y-1.5">
                {files.map((file, i) => (
                  <li
                    key={`${file.name}-${i}`}
                    className="flex items-center gap-2 rounded-md bg-muted/50 px-2.5 py-1.5 text-sm"
                  >
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">{file.name}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {(file.size / 1024).toFixed(0)} KB
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="shrink-0 text-muted-foreground hover:text-foreground"
                      aria-label={`Remove ${file.name}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="topic-icon">Icon (optional emoji)</Label>
            <Input
              id="topic-icon"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="e.g., dragon emoji"
              maxLength={10}
              className="w-24"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || isPending}>
            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {uploading
              ? 'Processing documents…'
              : isEditing
                ? 'Save Changes'
                : 'Create Topic'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
